import { Button } from "reactstrap"
import { ITempBillingImportsData } from "../../interfaces"
import { formatDateUSWithMoment } from "../../utils/commonFn"
import { COLUMN_INTERFACE_TYPES_FOR_TEMP_BILLING_IMPORTS, PAGE_LIMIT, TEMP_BILLING_IMPORTS_HEADER, WEEKLY_ACTIVITY_REPORT_TYPE } from "../../utils/constant"
import TableComponent from "../layout/TableComponent"
import { useCallback, useEffect, useState } from "react"
import InputField from "../layout/InputField"
import { useLazyQuery, useMutation } from "@apollo/client"
import { DELETE_TEMP_BILLING_IMPORT, LISTING_TEMP_DATA_IMPORTS } from "./gql"
import NoDataFound from "../layout/NoDataFound"
import Loader from "../layout/Loader"
import CustomButton from "../layout/CustomButton"
import CreateTempBillingReportModal from "./CreateModal"
import { useForm } from "react-hook-form"
import moment from "moment"
import { TEMP_BILLING_IMPORT } from "../../constants"
import DownloadButton from "../DownloadButton"
import ConfirmPopUp from "../ConfirmPopUp"
import { toast } from "react-toastify"
import DELETE_ICON from "../../assets/images/delete-icon-menu.svg"
import WeeklyActivitySheetModal from "./WeeklyActivitySheetModal"
import Pagination from "../layout/Pagination"

let time = 0
const AllTempBillingReports = () => {
  const defaultColumnFilters: COLUMN_INTERFACE_TYPES_FOR_TEMP_BILLING_IMPORTS = {
    monthYrSearch: '',
    fileNameSearch: '',
    reportTypeSearch: '',
    sorting: {
      columnName: '',
      columnValue: ''
    }
  };
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtering, setFiltering] = useState<boolean>(false)
  const [columnFilters, setColumnFilters] =
    useState<COLUMN_INTERFACE_TYPES_FOR_TEMP_BILLING_IMPORTS>(defaultColumnFilters);

  const [listTempDataImports, { data: listData, loading: listLoading, refetch }] = useLazyQuery<{ dataImportTempBillings: { dataImports: ITempBillingImportsData[], count: number } }>(LISTING_TEMP_DATA_IMPORTS);
  const { register, setValue } = useForm()
  const [deleteId, setDeleteId] = useState<string>('')
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false)

  const [deleteRecord, { loading: deleting }] = useMutation(DELETE_TEMP_BILLING_IMPORT, {
    onCompleted: () => {
      toast.success("Report deleted")
      setConfirmOpen(false)
      setDeleteId('')
      refetch()
    },
    // kan-101: a Recruiter Profit Summary with reconciled invoices can no longer
    // be deleted, and the server explains why — surface that instead of swallowing it.
    onError: (error) => toast.error(error?.message || "something went wrong"),
  });

  // kan-106: TableComponent sends the header LABEL + 'Sort'; map it to the
  // entity column the server sorts on. Only 'File Name' has a sort arrow today.
  const SORT_COLUMN_BY_LABEL: Record<string, string> = { 'File Name': 'fileName' }

  const changeFiled = (fieldName, value) => {
    // Any sort/filter change invalidates the current page position.
    setCurrentPage(1)
    if (fieldName?.endsWith('Sort')) {
      setFiltering(false)
      const label = fieldName.replace(/Sort$/, '');
      setColumnFilters((prevFilters) => {
        const { sorting, ...otherFilters } = prevFilters;
        return {
          ...otherFilters,
          sorting: {
            columnName: SORT_COLUMN_BY_LABEL[label] ?? label,
            columnValue: value ? 'ASC' : 'DESC',
          },
        };
      });
    } else {
      // Only typed text is debounced; the type select and date picker apply at once.
      setFiltering(fieldName === 'fileNameSearch')
      setColumnFilters((prevFilters) => ({
        ...prevFilters,
        [fieldName]: value,
      }));
    }
  };

  const mapCSVListData = ({
    id, reportMetaId, fileLink, fileName, uploadedBy: { fullName }, monthYr, createdAt, reportType, deleteBlocked
  }: ITempBillingImportsData) => {
    return (
      <>
        <td>{fileName || '--'}</td>
        <td>{reportType || '--'} </td>
        <td>{monthYr ? moment(monthYr).format('MMM-YYYY') : '--'} </td>
        <td> {fullName || '--'} </td>
        <td>{formatDateUSWithMoment(createdAt)}</td>
        <td>
          {/* kan-89: legacy imports have no reportMetaId, so the sheet can't be built for them. */}
          {reportType === WEEKLY_ACTIVITY_REPORT_TYPE && !!reportMetaId &&
            <WeeklyActivitySheetModal metaDataId={reportMetaId} />}
          <DownloadButton nickName={fileName} url={fileLink!} />
          {/* kan-101: a profit-summary import with reconciliation rows is evidence for a
              payment, so the server refuses to delete it. Disable rather than let the
              executive click into a guaranteed error. */}
          <Button
            className="bg-transparent border-0 p-0"
            disabled={!!deleteBlocked}
            title={deleteBlocked ? 'This payment has reconciled invoices and can no longer be deleted.' : undefined}
            onClick={() => {
              if (deleteBlocked) return
              setDeleteId(id)
              setConfirmOpen(true)
            }}
          >
            <img
              src={DELETE_ICON}
              style={deleteBlocked ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
              alt="delete" />
          </Button>
        </td>
      </>
    )
  }

  const listTempDataImportsCallback = useCallback(async () => {
    // kan-106: always send every active filter (all AND-ed server-side); empty
    // ones are dropped so the server falls back to its default (report date DESC).
    const activeFilters: Record<string, unknown> = {}
    if (columnFilters?.monthYrSearch) {
      activeFilters.monthYrSearch = moment(columnFilters.monthYrSearch).format("YYYY-MM")
    }
    const fileNameSearch = columnFilters?.fileNameSearch?.trim()
    if (fileNameSearch) activeFilters.fileNameSearch = fileNameSearch
    if (columnFilters?.reportTypeSearch) activeFilters.reportTypeSearch = columnFilters.reportTypeSearch
    if (columnFilters?.sorting?.columnName) activeFilters.sorting = columnFilters.sorting

    const fetchPage = () => listTempDataImports({
      variables: {
        payload: {
          limit,
          page: currentPage,
        },
        columnFilters: activeFilters,
      },
    })

    window.clearTimeout(time);
    if (filtering) {
      time = window.setTimeout(fetchPage, 1000);
    } else {
      await fetchPage();
    }
  }, [currentPage, limit, columnFilters]);

  useEffect(() => {
    listTempDataImportsCallback()
  }, [listTempDataImportsCallback])

  // Deleting the last row on the last page leaves an empty page (and hides the
  // pager); step back to the new last page instead.
  const totalCount = listData?.dataImportTempBillings?.count ?? 0
  const pageRows = listData?.dataImportTempBillings?.dataImports?.length ?? 0
  useEffect(() => {
    if (!listLoading && currentPage > 1 && totalCount > 0 && pageRows === 0) {
      setFiltering(false)
      setCurrentPage(Math.max(1, Math.ceil(totalCount / limit)))
    }
  }, [listLoading, currentPage, totalCount, pageRows, limit])

  return <>
    <div className="d-flex m- my-4" >
      <h5>{TEMP_BILLING_IMPORT}</h5>
      <CustomButton className="ml-auto" buttonText="Add"
        buttonClick={() => setIsOpen(!isOpen)}
      />
    </div>

    <div className="table-responsive placement-table">
      <TableComponent
        advanceSearching={true}
        handleChange={changeFiled}
        register={register}
        setValue={setValue}
        tableHeaderAdvSeacrh={TEMP_BILLING_IMPORTS_HEADER}>
        <tbody>
          {listLoading && (
            <td colSpan={14}>
              <Loader loaderClass="sm-height " />
            </td>
          )}
          {!listLoading && !listData?.dataImportTempBillings?.dataImports?.length && (
            <td colSpan={14} className="empty-table-td">
              <NoDataFound text="No Record Found!" />
            </td>
          )}
          {!listLoading && listData?.dataImportTempBillings?.dataImports?.map((item: ITempBillingImportsData, index: number) => {
            return <tr key={index}>{mapCSVListData(item)}</tr>
          })}
        </tbody>
      </TableComponent>
    </div >

    {!listLoading && !!listData?.dataImportTempBillings?.dataImports?.length &&
      <div className="users-pagination py-3" >
        <Pagination
          onPageChange={(pageClicked: number) => {
            setFiltering(false)
            setCurrentPage(pageClicked);
          }} currentPage={currentPage}
          pageCount={Math.ceil(listData?.dataImportTempBillings?.count / limit)}
        />
        <div className="d-flex align-items-center">
          <div className="pagination-display paginationItemsSelect">
            <p className="m-0">
              {(currentPage - 1) * limit + 1} to{' '}
              {Math.min(
                currentPage * limit,
                listData?.dataImportTempBillings?.count
              )}
              {` of `} {listData?.dataImportTempBillings?.count}
            </p>

            <div className="divider"></div>
            <label>Display</label>
            <InputField
              label=""
              inputtype="select"
              selectItems={PAGE_LIMIT.filter((item) => item.value !== limit)}
              inputid="limit"
              placeholder={limit.toString()}
              inputMethod={(value) => {
                setFiltering(false)
                setCurrentPage(1);
                setLimit(parseInt(value));
              }}
            />
          </div>
        </div>
      </ div>}
    <CreateTempBillingReportModal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} refetch={refetch} />
    <ConfirmPopUp
      confirmText="Are you sure you want to delete this report?"
      isOpen={confirmOpen}
      toggle={() => setConfirmOpen(!confirmOpen)}
      confirmAction={() => deleteRecord({ variables: { id: deleteId } })}
      modalHeading="Delete Report"
      btnText="Delete"
      btnColor="primary"
      loading={deleting}
    />
  </>

}

export default AllTempBillingReports